import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSettings, FiVolume2, FiVolumeX } from "react-icons/fi";
import { setSoundEnabled, playClickSound, playHoverSound } from "../utils/sound";
import "./ThemeCustomizer.css";

const themes = {
  violet: {
    violet: "#7c3aed",
    cyan: "#06b6d4",
    name: "Midnight Cyan"
  },
  sunset: {
    violet: "#f97316",
    cyan: "#ec4899",
    name: "Sunset Rose"
  },
  emerald: {
    violet: "#10b981",
    cyan: "#f59e0b",
    name: "Emerald Amber"
  },
  cyberpunk: {
    violet: "#ff007f",
    cyan: "#7928ca",
    name: "Neon Cyberpunk"
  }
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "124, 58, 237";
};

const ThemeCustomizer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState("violet");
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    // Load persisted settings
    const savedTheme = localStorage.getItem("portfolio-theme");
    if (savedTheme && themes[savedTheme]) {
      setActiveTheme(savedTheme);
      applyThemeStyles(savedTheme);
    }
    const savedSound = localStorage.getItem("portfolio-sound") === "true";
    setSoundOn(savedSound);
    setSoundEnabled(savedSound);
  }, []);

  const applyThemeStyles = (themeKey) => {
    const selectedTheme = themes[themeKey];
    if (!selectedTheme) return;
    const root = document.documentElement;
    root.style.setProperty("--accent-violet", selectedTheme.violet);
    root.style.setProperty("--accent-cyan", selectedTheme.cyan);
    root.style.setProperty("--accent-gradient", `linear-gradient(135deg, ${selectedTheme.violet}, ${selectedTheme.cyan})`);
    root.style.setProperty("--accent-gradient-reverse", `linear-gradient(135deg, ${selectedTheme.cyan}, ${selectedTheme.violet})`);
    root.style.setProperty("--border-accent", `rgba(${hexToRgb(selectedTheme.violet)}, 0.3)`);
    root.style.setProperty("--shadow-glow", `0 0 30px rgba(${hexToRgb(selectedTheme.violet)}, 0.15)`);
  };

  const handleThemeChange = (themeKey) => {
    setActiveTheme(themeKey);
    applyThemeStyles(themeKey);
    localStorage.setItem("portfolio-theme", themeKey);
    playClickSound();
  };

  const handleSoundToggle = () => {
    const nextSoundState = !soundOn;
    setSoundOn(nextSoundState);
    setSoundEnabled(nextSoundState);
    localStorage.setItem("portfolio-sound", nextSoundState.toString());
    if (nextSoundState) {
      // Play a verification beep right after turning it ON
      setTimeout(() => {
        playClickSound();
      }, 50);
    }
  };

  return (
    <div className="theme-customizer-container">
      {/* Toggle button */}
      <motion.button
        className="customizer-toggle-btn"
        onClick={() => {
          setIsOpen(!isOpen);
          playClickSound();
        }}
        whileHover={{ scale: 1.1, rotate: 45 }}
        whileTap={{ scale: 0.9 }}
      >
        <FiSettings className="settings-icon" />
      </motion.button>

      {/* Slide out panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="customizer-panel"
            initial={{ x: "110%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "110%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
          >
            <div className="customizer-header">
              <h3>Customizer</h3>
            </div>

            <div className="customizer-section">
              <h4>Accent Color</h4>
              <div className="theme-options">
                {Object.keys(themes).map((themeKey) => {
                  const t = themes[themeKey];
                  return (
                    <motion.button
                      key={themeKey}
                      className={`theme-swatch ${activeTheme === themeKey ? "active" : ""}`}
                      onClick={() => handleThemeChange(themeKey)}
                      onMouseEnter={playHoverSound}
                      title={t.name}
                      style={{
                        background: `linear-gradient(135deg, ${t.violet}, ${t.cyan})`
                      }}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  );
                })}
              </div>
              <span className="theme-name-label">{themes[activeTheme]?.name}</span>
            </div>

            <div className="customizer-section">
              <h4>Audio Feedback</h4>
              <div className="sound-toggle-wrapper">
                <motion.button
                  className={`sound-toggle-btn ${soundOn ? "sound-on" : "sound-off"}`}
                  onClick={handleSoundToggle}
                  onMouseEnter={playHoverSound}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {soundOn ? (
                    <>
                      <FiVolume2 className="sound-icon" />
                      <span>Audio On</span>
                    </>
                  ) : (
                    <>
                      <FiVolumeX className="sound-icon" />
                      <span>Audio Off</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeCustomizer;
